/**
 * Сессия Telegram-аккаунта — общая часть личного и общего (support) стора.
 * DEV-1712.
 *
 * Личный стор держит ещё чаты, сообщения и звонки, а карточке поддержки нужна
 * только сессия. Поэтому статус, QR-логин, 2FA и выход живут здесь, а оба
 * стора собираются поверх одной фабрики — иначе фильтр по login_id пришлось бы
 * писать дважды и он бы разошёлся.
 */
import { computed, ref } from 'vue'
import type {
  TelegramAccountStatus,
  TelegramStatusResponse,
  TelegramStartLoginResponse,
  TelegramSubmitPasswordResponse,
  TelegramLogoutResponse,
  TelegramLoginState,
  WsTelegramQrUpdatePayload,
  WsTelegramLoginStatusPayload,
  WsTelegramAccountStatusPayload,
  WsTelegramAccountSnapshot,
} from '@/features/messenger/types/telegram.types'

/** login_id общего аккаунта: бэкенд штампует им каждое WS-событие логина. */
export const SUPPORT_LOGIN_ID = 'support'

export type TelegramSessionScope = 'personal' | 'support'

export interface TelegramSessionApi {
  getStatus: () => Promise<TelegramStatusResponse>
  startLogin: () => Promise<TelegramStartLoginResponse>
  submitPassword: (params: {
    loginId: string
    password: string
  }) => Promise<TelegramSubmitPasswordResponse>
  logout: () => Promise<TelegramLogoutResponse>
}

export interface TelegramSessionOptions {
  scope: TelegramSessionScope
  api: TelegramSessionApi
  /** Личный стор гасит на выходе ещё и чаты — сессия про них не знает. */
  onLoggedOut?: () => void
}

/**
 * Код ошибки для интерфейса. Технический текст в состояние не кладём: строку
 * по коду подбирает слой интерфейса через i18n.
 */
export type TelegramSessionErrorCode =
  | 'wrong_password' // неверный 2FA-пароль (gRPC InvalidArgument)
  | 'login_expired' // сервер уже не ждёт пароля (gRPC FailedPrecondition)
  | 'already_connected' // вход при живой сессии (gRPC FailedPrecondition)
  | 'unavailable' // провайдер выключен (HTTP 503)
  | 'logout_failed' // отключение не прошло
  | 'status_failed' // состояние аккаунта прочитать не удалось
  | 'calls_crm_only_failed' // настройка звонков Telegram не применилась (DEV-2741)
  | 'generic'

// Оба кода gateway отдаёт как HTTP 400 — различаем их по телу ответа.
const GRPC_INVALID_ARGUMENT = 3
const GRPC_FAILED_PRECONDITION = 9

const httpStatus = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status

/** DefaultHTTPErrorHandler кладёт числовой gRPC-код в тело: {"code":N,...}. */
const grpcCode = (error: unknown): number | undefined =>
  (error as { response?: { data?: { code?: number } } } | null)?.response?.data?.code

const logRawError = (action: string, error: unknown): void =>
  console.error(`[telegram] ${action} failed`, error)

/** Сырую ошибку пишем в консоль, наружу отдаём код для интерфейса. */
export function reportTelegramError(
  action: string,
  error: unknown,
  fallback: TelegramSessionErrorCode = 'generic',
): TelegramSessionErrorCode {
  logRawError(action, error)
  return httpStatus(error) === 503 ? 'unavailable' : fallback
}

/**
 * Коды ответа на пароль. Голый 400 без кода в теле неотличим: это и «неверный
 * пароль», и «пароля уже не ждём», — обещать переввод в этой ветке нельзя.
 */
function passwordErrorCode(error: unknown): TelegramSessionErrorCode {
  const code = grpcCode(error)
  if (code === GRPC_INVALID_ARGUMENT) return 'wrong_password'
  if (code === GRPC_FAILED_PRECONDITION) return 'login_expired'
  return httpStatus(error) === 503 ? 'unavailable' : 'generic'
}

/**
 * Коды старта входа. «Уже подключён» опознаём только по коду 9 в теле: голый
 * 400 без кода — неизвестный отказ, гадать про состояние аккаунта не вправе.
 */
function startLoginErrorCode(error: unknown): TelegramSessionErrorCode {
  if (grpcCode(error) === GRPC_FAILED_PRECONDITION) return 'already_connected'
  return httpStatus(error) === 503 ? 'unavailable' : 'generic'
}

export function createTelegramSession({ scope, api, onLoggedOut }: TelegramSessionOptions) {
  const status = ref<TelegramAccountStatus>('inactive')
  const account = ref<TelegramStatusResponse | null>(null)
  const isLoadingStatus = ref(false)
  const isConnected = computed(() => status.value === 'active')
  /** Статус хотя бы раз прочитан с сервера: до этого status — не факт, а заглушка. */
  const isStatusKnown = ref(false)

  const loginState = ref<TelegramLoginState>('idle')
  const loginId = ref<string | null>(null)
  // Номер попытки входа. loginId для этого не годится: у общего аккаунта он
  // всегда 'support', у личного — всегда uuid менеджера, то есть константа.
  let loginAttempt = 0
  const qrText = ref<string | null>(null)
  const qrImageBase64 = ref<string | null>(null)
  const qrExpiresAt = ref<string | null>(null)
  const loginError = ref<TelegramSessionErrorCode | null>(null)
  const isSubmittingPassword = ref(false)

  const error = ref<TelegramSessionErrorCode | null>(null)

  /**
   * Чьё это событие логина. Общий аккаунт всегда помечен 'support', личный —
   * uuid менеджера либо ничем (старый бэкенд поле не слал).
   */
  const isOwnLogin = (eventLoginId?: string): boolean =>
    scope === 'support' ? eventLoginId === SUPPORT_LOGIN_ID : eventLoginId !== SUPPORT_LOGIN_ID

  /** true — статус подтверждён сервером, false — прочитать не удалось. */
  async function loadStatus(): Promise<boolean> {
    isLoadingStatus.value = true
    error.value = null
    try {
      const resp = await api.getStatus()
      account.value = resp
      applyAccountStatus(resp.status)

      return true
    } catch (e) {
      // Состояние неизвестно, а не «не привязан»: прежние значения не трогаем.
      // 503 разбираем как везде — «Telegram недоступен» вместо предложения повтора.
      error.value = reportTelegramError('getStatus', e, 'status_failed')

      return false
    } finally {
      isLoadingStatus.value = false
    }
  }

  /**
   * Перечитать статус после того, как сервер опроверг наше знание. Не вышло —
   * снимаем уверенность: экран скажет «состояние неизвестно», а не прежнее.
   */
  async function refreshStatus(): Promise<boolean> {
    const isRead = await loadStatus()
    if (!isRead) isStatusKnown.value = false

    return isRead
  }

  async function startLogin() {
    loginAttempt += 1
    loginState.value = 'loading'
    loginError.value = null
    qrText.value = null
    qrImageBase64.value = null
    try {
      const resp = await api.startLogin()
      loginId.value = resp.loginId
      qrText.value = resp.qrText
      qrImageBase64.value = resp.imageBase64 ?? null
      qrExpiresAt.value = resp.expiresAt
      loginState.value = 'qr'
    } catch (e) {
      logRawError('startLogin', e)
      const code = startLoginErrorCode(e)
      if (code === 'already_connected') {
        // Сервер утверждает, что сессия жива: не спорим с ним, а перечитываем
        // статус — иначе экран остаётся на «не привязан» до перезагрузки.
        const isRead = await refreshStatus()
        if (isRead && isConnected.value) {
          loginState.value = 'success'
          loginError.value = null
          return
        }
      }
      loginError.value = code
      loginState.value = 'error'
    }
  }

  async function submitPassword(password: string) {
    const attemptLoginId = loginId.value
    if (!attemptLoginId) return
    const attempt = loginAttempt
    isSubmittingPassword.value = true
    loginError.value = null
    try {
      const resp = await api.submitPassword({ loginId: attemptLoginId, password })
      applyAccountStatus(resp.status)
      // Шаг двигаем только своей попытке: пока ждали ответ, вход могли начать заново.
      if (resp.status === 'active' && attempt === loginAttempt) loginState.value = 'success'
    } catch (e) {
      logRawError('submitPassword', e)
      const code = passwordErrorCode(e)
      // Пока ждали ответ, модалку могли закрыть или начать новый вход: у той
      // попытки свой номер, и к её флоу этот ответ отношения не имеет.
      const isSameAttempt = attempt === loginAttempt
      if (code === 'login_expired') {
        if (!isSameAttempt) {
          // Своей попытки входа больше нет — второй QR никому не нужен,
          // а настоящее состояние аккаунта знает только сервер.
          await refreshStatus()
          return
        }
        // Человеку нужен свежий QR, а не переввод правильного пароля.
        resetLogin()
        await startLogin()
        if (loginState.value === 'qr') {
          // Свежий QR действительно ждёт скана — только теперь «нужен пароль» точно неверен.
          if (status.value === 'password_needed') status.value = 'pending_qr'
          // Подсказка нужна только на свежем QR: чужого состояния не затираем.
          if (!loginError.value) loginError.value = code
        } else if (loginState.value === 'error' && loginError.value !== 'already_connected') {
          // Перезапуска нет — ожидание QR было бы догадкой, спрашиваем сервер.
          // «Уже подключён» сюда не идёт: та ветка статус уже перечитала.
          await refreshStatus()
        }
      } else if (isSameAttempt) {
        // Шаг пароля не сбрасываем: человек правит опечатку в том же поле.
        loginError.value = code
      }
    } finally {
      isSubmittingPassword.value = false
    }
  }

  function resetLogin() {
    // Прежняя попытка закрыта: её поздний ответ новый флоу двигать не должен.
    loginAttempt += 1
    loginState.value = 'idle'
    loginId.value = null
    qrText.value = null
    qrImageBase64.value = null
    qrExpiresAt.value = null
    loginError.value = null
  }

  /** true — сервер подтвердил отключение. Вызывающий обязан смотреть на ответ. */
  async function logout(): Promise<boolean> {
    error.value = null
    try {
      await api.logout()
    } catch (e) {
      // Сессия на сервере жива — локальное состояние не гасим.
      error.value = reportTelegramError('logout', e, 'logout_failed')
      return false
    }
    status.value = 'inactive'
    account.value = null
    // Отключение подтверждено сервером — состояние снова известно точно.
    isStatusKnown.value = true
    onLoggedOut?.()
    resetLogin()

    return true
  }

  function handleQrUpdate(payload: WsTelegramQrUpdatePayload) {
    if (payload.login_id !== loginId.value) return
    qrText.value = payload.qr_text
    qrImageBase64.value = payload.image_base64 ?? null
    qrExpiresAt.value = payload.expires_at
    if (loginState.value === 'loading') loginState.value = 'qr'
  }

  /** Статус, подтверждённый сервером: это такое же знание, как ответ getStatus. */
  function applyAccountStatus(next: TelegramAccountStatus) {
    status.value = next
    isStatusKnown.value = true
  }

  /** Аккаунт из снимка PROVIDER_STATUS (DEV-2227) — то же знание, что ответ getStatus. */
  function applyAccountSnapshot(snapshot: WsTelegramAccountSnapshot) {
    applyAccountStatus(snapshot.status)
    account.value = {
      ...account.value,
      status: snapshot.status,
      username: snapshot.username,
      phone: snapshot.phone,
    }
  }

  /**
   * Шаг флоу от сервера. Ошибку гасим только на смене шага: бэкенд шлёт
   * password_needed и на каждый неверный пароль, а повтор шага отказ не отменяет.
   */
  function advanceLoginState(next: TelegramLoginState) {
    if (loginState.value === next) return
    loginState.value = next
    loginError.value = null
  }

  /**
   * Вернуться к вводу пароля без нового QR. Ручке пароля общего аккаунта
   * login_id не нужен, личной — нужен, а после закрытия окна он потерян.
   */
  const canResumePasswordEntry = computed(
    () => scope === 'support' && status.value === 'password_needed',
  )

  /** false — такого шага сессия предложить не может, обещать его нельзя. */
  function resumePasswordEntry(): boolean {
    if (!canResumePasswordEntry.value) return false
    loginAttempt += 1
    loginId.value = SUPPORT_LOGIN_ID
    qrText.value = null
    qrImageBase64.value = null
    qrExpiresAt.value = null
    loginError.value = null
    loginState.value = 'password'

    return true
  }

  function handleLoginStatus(payload: WsTelegramLoginStatusPayload) {
    // Событие чужого логина: без этой проверки подключение общего аккаунта
    // перекрашивало бы личную карточку менеджера, и наоборот.
    if (!isOwnLogin(payload.login_id)) return
    applyAccountStatus(payload.status)
    if (payload.status === 'password_needed') {
      advanceLoginState('password')
    } else if (payload.status === 'active') {
      advanceLoginState('success')
      if (payload.phone || payload.username) {
        // Событие приходит и при каждом переподключении раннера: прочитанные из
        // статуса поля того же аккаунта (callsCrmOnly, DEV-2741) не теряем. Вход
        // другим аккаунтом (повторный вход после отзыва, другая вкладка) старое
        // не наследует — телефон и username в событии и статусе из одного источника.
        const prev = account.value
        const isSameAccount = prev?.phone === payload.phone && prev?.username === payload.username
        account.value = {
          ...(isSameAccount ? prev : null),
          status: 'active',
          phone: payload.phone,
          username: payload.username,
        }
      }
    } else if (payload.status === 'error' || payload.status === 'revoked') {
      loginState.value = 'error'
      // message события — технический текст сервера, в состоянии держим код.
      logRawError('login status', payload)
      loginError.value = 'generic'
    }
  }

  /**
   * Подключённый аккаунт поменял состояние сам — Telegram отозвал сессию
   * (DEV-2143). Чей это аккаунт, решает маршрутизатор WS по login_id.
   */
  function handleAccountStatus(payload: WsTelegramAccountStatusPayload) {
    applyAccountStatus(payload.status)
    if (account.value) account.value.status = payload.status
  }

  return {
    status,
    account,
    isLoadingStatus,
    isStatusKnown,
    isConnected,
    loginState,
    loginId,
    qrText,
    qrImageBase64,
    qrExpiresAt,
    loginError,
    isSubmittingPassword,
    canResumePasswordEntry,
    error,
    loadStatus,
    startLogin,
    submitPassword,
    resumePasswordEntry,
    resetLogin,
    logout,
    applyAccountStatus,
    applyAccountSnapshot,
    handleQrUpdate,
    handleLoginStatus,
    handleAccountStatus,
  }
}

export type TelegramSession = ReturnType<typeof createTelegramSession>
